import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown, ArrowUpRight } from "lucide-react";

const SERVICE_LINKS = [
  { to: "/commission-clinical-services", label: "Commission Services", tag: "DIV 01", desc: "Managed clinical pharmacy services for PCNs & GP practices", testid: "nav-link-commission" },
  { to: "/request-clinical-workforce", label: "Request Workforce", tag: "DIV 02", desc: "Pharmacists & pharmacy technicians for your team", testid: "nav-link-workforce" },
  { to: "/transport", label: "Transport Services", tag: "DIV 03", desc: "Home-to-school, SEND & specialist passenger transport", testid: "nav-link-transport" },
  { to: "/arrs-nhs-support", label: "ARRS & NHS Support", tag: "DIV 04", desc: "Additional Roles Reimbursement Scheme support", testid: "nav-link-arrs" },
  { to: "/out-of-hours", label: "NCS Out of Hours", tag: "DIV 05", desc: "Overnight palliative medication supply pathway", testid: "nav-link-ooh" },
  { to: "/community-pharmacy", label: "Community Pharmacy", tag: "DIV 06", desc: "Locum cover & pharmacist workforce solutions", testid: "nav-link-community" },
  { to: "/ncs-clinic", label: "NCS Clinic", tag: "DEV", desc: "A developing pharmacist-led private clinic network", testid: "nav-link-clinic" },
];

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/international", label: "International", testid: "nav-link-international" },
  { to: "/insights", label: "Insights", testid: "nav-link-insights" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/careers", label: "Careers", testid: "nav-link-careers" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

const MOBILE_LINKS = [LINKS[0], ...SERVICE_LINKS, ...LINKS.slice(1)];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const servicesRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const servicesActive = SERVICE_LINKS.some((l) => l.to === location.pathname);

  const linkCls = (isActive) =>
    `relative px-2.5 py-2 text-[12.5px] font-medium rounded-full whitespace-nowrap transition-colors duration-300 ${
      isActive ? "text-[#0B192C]" : "text-[#64748B] hover:text-[#0B192C]"
    }`;

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#FBFBFA]/90 backdrop-blur-xl border-b border-[#0B192C]/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
            <img
              src="/assets/ncs-logo-full.png?v=4"
              alt="NCS — National Clinical Services"
              width="135"
              height="64"
              className="h-10 xl:h-11 w-auto max-w-none object-contain object-left"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-0" data-testid="desktop-nav">
            <NavLink to="/" data-testid="nav-link-home" className={({ isActive }) => linkCls(isActive)}>
              {({ isActive }) => (
                <>
                  Home
                  {isActive && (
                    <motion.span
                      layoutId="nav-dot"
                      className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-4 h-px bg-[#C5A059]"
                    />
                  )}
                </>
              )}
            </NavLink>

            {/* Services mega menu */}
            <div
              ref={servicesRef}
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                data-testid="nav-services-dropdown"
                aria-expanded={servicesOpen}
                onClick={() => setServicesOpen((v) => !v)}
                className={`relative inline-flex items-center gap-1 px-2.5 py-2 text-[12.5px] font-medium rounded-full whitespace-nowrap transition-colors duration-300 ${
                  servicesActive ? "text-[#0B192C]" : "text-[#64748B] hover:text-[#0B192C]"
                }`}
              >
                Services
                <ChevronDown size={13} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                {servicesActive && (
                  <motion.span
                    layoutId="nav-dot"
                    className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-4 h-px bg-[#C5A059]"
                  />
                )}
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.18 }}
                    data-testid="nav-services-menu"
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[540px] bg-white border border-[#0B192C]/10 rounded-2xl shadow-[0_24px_60px_-20px_rgba(11,25,44,0.25)] p-3"
                  >
                    <div className="grid grid-cols-2 gap-1">
                      {SERVICE_LINKS.map((l) => (
                        <NavLink
                          key={l.to}
                          to={l.to}
                          data-testid={l.testid}
                          className={({ isActive }) =>
                            `group block px-4 py-3 rounded-xl transition-colors duration-200 ${
                              isActive ? "bg-[#F4F4F0]" : "hover:bg-[#F4F4F0]"
                            }`
                          }
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="text-[13px] font-semibold text-[#0B192C]">{l.label}</span>
                            <span className="font-mono-brand text-[9px] tracking-[0.14em] text-[#C5A059]">{l.tag}</span>
                          </span>
                          <span className="mt-0.5 block text-[11px] leading-snug text-[#64748B]">{l.desc}</span>
                        </NavLink>
                      ))}
                      <NavLink
                        to="/international"
                        className="group block px-4 py-3 rounded-xl transition-colors duration-200 hover:bg-[#F4F4F0]"
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-[13px] font-semibold text-[#0B192C]">NCS International</span>
                          <span className="font-mono-brand text-[9px] tracking-[0.14em] text-[#C5A059]">INTL</span>
                        </span>
                        <span className="mt-0.5 block text-[11px] leading-snug text-[#64748B]">Middle East healthcare partnerships</span>
                      </NavLink>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {LINKS.slice(1).map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.testid} className={({ isActive }) => linkCls(isActive)}>
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-dot"
                        className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-4 h-px bg-[#C5A059]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-4 shrink-0">
            <Link
              to="/contact"
              data-testid="nav-cta-speak"
              className="group inline-flex items-center gap-2 bg-[#0B192C] text-white text-[13px] font-medium px-5 py-2.5 rounded-full whitespace-nowrap transition-colors duration-300 hover:bg-[#0F382C]"
            >
              Speak to Our Team
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden w-11 h-11 rounded-full border border-[#0B192C]/15 bg-white text-[#0B192C] flex items-center justify-center transition-colors duration-300 hover:border-[#0F382C]"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-[#FBFBFA]/98 backdrop-blur-xl border-t border-[#0B192C]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {MOBILE_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `block py-2.5 font-display text-xl font-semibold ${
                        isActive ? "text-[#0F382C]" : "text-[#0B192C]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-[#0B192C] text-white font-medium px-6 py-3.5 rounded-full"
              >
                Speak to Our Team <ArrowRight size={16} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
